import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { take } from 'rxjs';
import { ClientCategoryControllerService, ClientClipControllerService, ClientClipStatsResponse, ClientMediumControllerService, ManagementClipResponse } from 'api-clipping';
import { UtilsService } from '../../../../shared/services/utils.service';

@Component({
	selector: 'app-client-clipping-stats',
	standalone: true,
	imports: [CommonModule, MatIconModule, MatTooltipModule, TranslateModule],
	templateUrl: './client-clipping-stats.component.html',
	styleUrl: './client-clipping-stats.component.scss',
})
export class ClientClippingStatsComponent implements OnInit, OnChanges {
	@Input() hasCompanyTags = false;
	@Input() reloadKey?: any;
	@Output() openClip = new EventEmitter<ManagementClipResponse>();

	stats?: ClientClipStatsResponse;
	mostValuableClip?: ManagementClipResponse;
	mostActiveMediumName = '';
	mostPresentCategoryName = '';
	loading = false;

	private categoriesCache: any[] | null = null;

	constructor(
		private apiClientClip: ClientClipControllerService,
		private apiClientCategory: ClientCategoryControllerService,
		private apiClientMedium: ClientMediumControllerService,
		public utils: UtilsService
	) {}

	ngOnInit(): void {
		this.loadStats();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['reloadKey'] && !changes['reloadKey'].firstChange) {
			this.loadStats();
		}
	}

	private loadStats(): void {
		this.loading = true;
		this.mostValuableClip = undefined;
		this.mostActiveMediumName = '';
		this.mostPresentCategoryName = '';
		this.apiClientClip
			.getClientClipsStats()
			.pipe(take(1))
			.subscribe({
				next: stats => {
					this.stats = stats;
					this.loading = false;
					if (this.hasCompanyTags && stats?.mostValuableClip) {
						this.apiClientClip
							.getClientClipById(stats.mostValuableClip)
							.pipe(take(1))
							.subscribe({
								next: clip => (this.mostValuableClip = clip),
							});
					}
					if (!this.hasCompanyTags) {
						if (stats?.mostPresentCategory?.id) this.resolveCategoryName(stats.mostPresentCategory.id);
						if (stats?.mostActiveMedium?.id) this.resolveMediumName(stats.mostActiveMedium.id);
					}
				},
				error: () => {
					this.stats = undefined;
					this.loading = false;
				},
			});
	}

	private resolveCategoryName(categoryId: string): void {
		const lookup = (categories: any[]) => {
			const match = categories?.find(c => c.id === categoryId);
			this.mostPresentCategoryName = match ? this.utils.getParameterByLanguageScript(match, 'name', true) || '' : '';
		};
		if (this.categoriesCache) {
			lookup(this.categoriesCache);
			return;
		}
		const loginData = JSON.parse(localStorage.getItem('loginData') || '{}');
		const clientId = loginData?.clientId;
		this.apiClientCategory
			.getCategoriesWithTagsForClient(clientId ? { clientId } : undefined)
			.pipe(take(1))
			.subscribe({
				next: categories => {
					this.categoriesCache = categories || [];
					lookup(this.categoriesCache);
				},
			});
	}

	private resolveMediumName(mediumId: string): void {
		this.apiClientMedium
			.getMediumById(mediumId)
			.pipe(take(1))
			.subscribe({
				next: medium => {
					this.mostActiveMediumName = medium ? this.utils.getParameterByLanguageScript(medium, 'name') || '' : '';
				},
			});
	}

	totalClipsDelta(): number {
		return (this.stats?.totalClips?.today ?? 0) - (this.stats?.totalClips?.yesterday ?? 0);
	}

	visibilityDeltaPercent(): number | null {
		const today = this.stats?.averageVisibilityIndex?.today;
		const yesterday = this.stats?.averageVisibilityIndex?.yesterday;
		if (today == null) return null;
		if (!yesterday) return today > 0 ? 100 : 0;
		return Math.round(((today - yesterday) / yesterday) * 100);
	}

	sentimentTotal(day: 'today' | 'yesterday' = 'today'): number {
		const b = this.stats?.sentiments?.[day];
		if (!b) return 0;
		return (b.positive ?? 0) + (b.neutral ?? 0) + (b.negative ?? 0);
	}

	sentimentPercent(part: 'positive' | 'neutral' | 'negative'): number {
		const total = this.sentimentTotal();
		if (!total) return 0;
		const value = this.stats?.sentiments?.today?.[part] ?? 0;
		return (value / total) * 100;
	}

	sentimentScoreDelta(): number {
		const today = this.stats?.totalSentimentScore?.today ?? 0;
		const yesterday = this.stats?.totalSentimentScore?.yesterday ?? 0;
		return today - yesterday;
	}

	mostValuableClipMedium(): string {
		return this.utils.getParameterByLanguageScript(this.mostValuableClip?.medium, 'name') || '';
	}

	mostValuableClipTitle(): string {
		return this.utils.getParameterByLanguageScript(this.mostValuableClip, 'title', true) || '';
	}

	hasShowSentimentCards(): boolean {
		return !!(this.stats?.totalClips || this.stats?.sentiments || this.stats?.averageVisibilityIndex);
	}

	hasNoSentimentCards(): boolean {
		return !!(this.stats?.totalClips || this.stats?.mostActiveMedium || this.stats?.mostPresentCategory);
	}

	emitOpenClip(): void {
		if (this.mostValuableClip) this.openClip.emit(this.mostValuableClip);
	}
}
